import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import purchasesService from '../services/purchasesService';
import { useNotification } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import ConfirmActionModal from '../components/ConfirmActionModal';
import RegisterPaymentModal from '../components/RegisterPaymentModal';
import '../styles/general.css';

const PER_PAGE = 50;

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    parseFloat(value) || 0
  );

const PurchaseOrdersListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled: a button the user can never
  // enable is its own kind of noise. The API rejects them regardless.
  const canWrite = canManage && canManage('purchases');


  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [ordering, setOrdering] = useState('-effective_date');
  const [showInactive, setShowInactive] = useState(false);

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // One modal drives both directions of the soft-delete flag; `togglingOrder`
  // carries its current is_active so the copy and the toast can follow it.
  const [togglingOrder, setTogglingOrder] = useState(null);
  const [payingOrder, setPayingOrder] = useState(null);
  const [processing, setProcessing] = useState(false);

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await purchasesService.listPurchaseOrders({
        search: search || null,
        effectiveDateStart: dateFrom || null,
        effectiveDateEnd: dateTo || null,
        categoryId: categoryId || null,
        subcategoryId: subcategoryId || null,
        ordering,
        page,
        includeInactive: showInactive
      });
      setOrders(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading purchase orders:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [
    search, dateFrom, dateTo, categoryId, subcategoryId,
    ordering, page, showInactive, notify, t
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await purchasesService.listPurchaseTypes({ type: 'CATEGORY' });
        setCategories(res.data || []);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Cascade: subcategories are only meaningful once a category is chosen.
  useEffect(() => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    const loadSubcategories = async () => {
      try {
        const res = await purchasesService.listPurchaseTypes({
          type: 'SUBCATEGORY',
          parentId: categoryId
        });
        setSubcategories(res.data || []);
      } catch (err) {
        console.error('Error loading subcategories:', err);
      }
    };
    loadSubcategories();
  }, [categoryId]);

  const handleCategoryChange = (value) => {
    setCategoryId(value);
    setSubcategoryId('');
    setPage(1);
  };

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleToggleActiveConfirm = async () => {
    if (!togglingOrder) return;
    setProcessing(true);
    try {
      // Cancelling uses DELETE (the REST-idiomatic soft delete); bringing an
      // order back needs the explicit action, since DELETE only ever clears.
      if (togglingOrder.is_active) {
        await purchasesService.cancelPurchaseOrder(togglingOrder.id);
        notify.success(t('purchases.orderCancelled'));
      } else {
        await purchasesService.toggleActive(togglingOrder.id);
        notify.success(t('purchases.orderReactivated'));
      }
      setTogglingOrder(null);
      loadOrders();
    } catch (err) {
      console.error('Error toggling purchase order active state:', err);
      notify.error(t('common.failedToUpdate'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading && orders.length === 0) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('purchases.title')}</h1>
        {canWrite && (
          <button onClick={() => navigate('/purchases/create')} className="btn btn-primary">
            {t('purchases.createButton')}
          </button>
        )}
      </div>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label" htmlFor="purchase-search">
              {t('filters.searchValue')}
            </label>
            <input
              id="purchase-search"
              type="text"
              value={search}
              onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
              placeholder={t('purchases.filters.searchPlaceholder')}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="purchase-date-from">
              {t('filters.dateFrom')}
            </label>
            <input
              id="purchase-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => handleFilterChange(setDateFrom)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="purchase-date-to">
              {t('filters.dateTo')}
            </label>
            <input
              id="purchase-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => handleFilterChange(setDateTo)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="purchase-category">
              {t('purchases.category')}
            </label>
            <select
              id="purchase-category"
              value={categoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="form-select"
            >
              <option value="">{t('purchases.filters.allCategories')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="purchase-subcategory">
              {t('purchases.subcategory')}
            </label>
            <select
              id="purchase-subcategory"
              value={subcategoryId}
              onChange={(e) => handleFilterChange(setSubcategoryId)(e.target.value)}
              disabled={!categoryId}
              className="form-select"
            >
              <option value="">{t('purchases.filters.allSubcategories')}</option>
              {subcategories.map((subcategory) => (
                <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="purchase-ordering">
              {t('filters.sortBy')}
            </label>
            <select
              id="purchase-ordering"
              value={ordering}
              onChange={(e) => handleFilterChange(setOrdering)(e.target.value)}
              className="form-select"
            >
              <option value="-effective_date">{t('purchases.filters.dateNewest')}</option>
              <option value="effective_date">{t('purchases.filters.dateOldest')}</option>
              <option value="supplier__name">{t('purchases.filters.supplierAZ')}</option>
              <option value="-supplier__name">{t('purchases.filters.supplierZA')}</option>
            </select>
          </div>

          <div className="filter-group filter-group-checkbox">
            <label className="form-label" htmlFor="purchase-show-inactive">
              <input
                id="purchase-show-inactive"
                type="checkbox"
                checked={showInactive}
                onChange={(e) => handleFilterChange(setShowInactive)(e.target.checked)}
              />
              {' '}{t('filters.showInactive')}
            </label>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>{t('purchases.orderNumber')}</th>
              <th>{t('purchases.supplierOrderNumber')}</th>
              <th>{t('purchases.invoiceReference')}</th>
              <th>{t('table.supplier')}</th>
              <th>{t('purchases.category')}</th>
              <th>{t('purchases.subcategory')}</th>
              <th>{t('purchases.effectiveDate')}</th>
              <th>{t('purchases.isPaid')}</th>
              {/* Pagado says the state, Cuenta says where. A paid row with
                  an empty Cuenta is an order from before this module. */}
              <th>{t('purchases.account')}</th>
              <th>{t('purchases.isInvoiced')}</th>
              <th>{t('table.total')}</th>
              {showInactive && <th>{t('common.status')}</th>}
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={showInactive ? 13 : 12} className="empty-state">
                  <p>{t('purchases.noPurchaseOrders')}</p>
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.order_number}</td>
                  <td>{order.supplier_order_number || '—'}</td>
                  <td>{order.invoice_reference || '—'}</td>
                  <td>{order.supplier_name}</td>
                  <td>{order.category_name}</td>
                  <td>{order.subcategory_name}</td>
                  <td>{order.effective_date}</td>
                  <td>
                    <span className="tag-badge">
                      {order.is_paid ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td>{order.monetary_account_name || '—'}</td>
                  <td>
                    <span className="tag-badge">
                      {order.is_invoiced ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td>{formatCurrency(order.total)}</td>
                  {showInactive && (
                    <td>
                      <span className="tag-badge">
                        {order.is_active
                          ? t('common.active')
                          : t('purchases.cancelledBadge')}
                      </span>
                    </td>
                  )}
                  <td className="table-actions">
                    <button
                      onClick={() => navigate(`/purchases/${order.id}`)}
                      className="btn btn-info btn-small"
                    >
                      {t('purchases.viewButton')}
                    </button>
                    {canWrite && order.is_active && (
                      <button
                        onClick={() => navigate(`/purchases/${order.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {canWrite && order.is_active && !order.is_paid && (
                      <button
                        onClick={() => setPayingOrder(order)}
                        className="btn btn-accent btn-small"
                      >
                        {t('purchases.registerPayment')}
                      </button>
                    )}
                    {canWrite && (
                      <button
                        onClick={() => setTogglingOrder(order)}
                        className={`btn btn-small ${order.is_active ? 'btn-danger' : 'btn-info'}`}
                      >
                        {order.is_active
                          ? t('purchases.cancelButton')
                          : t('purchases.reactivateButton')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">{t('common.page')} {page}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!togglingOrder}
        onClose={() => setTogglingOrder(null)}
        onConfirm={handleToggleActiveConfirm}
        title={togglingOrder?.is_active
          ? t('purchases.cancelButton')
          : t('purchases.reactivateButton')}
        message={togglingOrder?.is_active
          ? t('purchases.confirmCancel')
          : t('purchases.confirmReactivate')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />

      <RegisterPaymentModal
        isOpen={!!payingOrder}
        onClose={() => setPayingOrder(null)}
        document={payingOrder && {
          id: payingOrder.id,
          number: payingOrder.order_number,
          total: payingOrder.total
        }}
        onSubmit={(payload) => purchasesService.markPaid(payingOrder.id, payload)}
        onPaid={() => {
          setPayingOrder(null);
          // Reload rather than patch the row: paying changes three fields
          // and the server is the one that knows them.
          loadOrders();
        }}
      />
    </div>
  );
};

export default PurchaseOrdersListPage;
